import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, User, Eye, EyeOff, Loader2, ShieldCheck, Check, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import AuthShell from '../components/AuthShell';
import GoogleSignInButton from '../components/GoogleSignInButton';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';

function Checkbox({ checked, onChange, children, testId }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer group">
      <button
        type="button"
        data-testid={testId}
        onClick={() => onChange(!checked)}
        aria-checked={checked}
        role="checkbox"
        className={`mt-0.5 w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all shadow-sm ${
          checked
            ? 'bg-[#2A9D8F] border-[#2A9D8F] shadow-[#2A9D8F]/30'
            : 'bg-white border-[#2B2D42] hover:border-[#C8553D] hover:bg-[#FFF8F5]'
        }`}
      >
        {checked && <Check className="w-4 h-4 text-white" strokeWidth={3.5} />}
      </button>
      <span className="text-sm text-[#2B2D42] leading-relaxed font-medium">{children}</span>
    </label>
  );
}

export default function Signup() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [guidelinesAccepted, setGuidelinesAccepted] = useState(false);
  const [verificationConsent, setVerificationConsent] = useState(false);

  function validate() {
    const newErrors = {};
    if (!name.trim()) newErrors.name = 'Name is required';
    if (!email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors.email = 'Invalid email format';
    if (!password) newErrors.password = 'Password is required';
    else if (password.length < 8) newErrors.password = 'Password must be at least 8 characters';
    if (password !== confirmPassword) newErrors.confirm = 'Passwords do not match';
    if (!termsAccepted || !guidelinesAccepted || !verificationConsent) {
      newErrors.consent = 'Please accept all required agreements to continue';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const data = await api('/auth/signup', {
        method: 'POST',
        body: JSON.stringify({
          email: email.toLowerCase().trim(),
          password,
          name: name.trim(),
          consent: {
            terms_accepted: termsAccepted,
            privacy_accepted: termsAccepted,
            community_guidelines_accepted: guidelinesAccepted,
            verification_consent: verificationConsent,
            policy_version: '1.0',
          },
        }),
      });

      await login(data.token, data.user);
      toast.success('Account created — check your email for the verification code');
      navigate('/app/verify', { state: { email: email.toLowerCase().trim() } });
    } catch (err) {
      toast.error(err.message || 'Signup failed');
    } finally {
      setLoading(false);
    }
  }

  function inputCls(hasErr) {
    return `flex items-center bg-white border rounded-lg px-4 py-3 focus-within:border-[#E07A5F] transition-colors ${
      hasErr ? 'border-[#E76F51]' : 'border-[#F4F1DE]'
    }`;
  }

  return (
    <AuthShell>
      <h1 className="text-3xl font-serif font-bold text-[#2B2D42]" style={{ fontFamily: 'Playfair Display, serif' }}>
        Create Account
      </h1>
      <p className="text-[#6D6875] mt-2 mb-6">Join the African diaspora community in Canada</p>

      <form onSubmit={handleSubmit} className="space-y-4" data-testid="signup-form">
        {/* Name */}
        <div>
          <label className="block text-sm font-bold text-[#2B2D42] mb-2">Your Name</label>
          <div className={inputCls(errors.name)}>
            <User className="w-5 h-5 text-[#B5B5B5]" />
            <input
              data-testid="signup-name-input"
              value={name}
              onChange={(e) => { setName(e.target.value); setErrors({ ...errors, name: undefined }); }}
              placeholder="Enter your name"
              autoComplete="name"
              className="flex-1 ml-3 outline-none bg-transparent text-[#2B2D42] placeholder-[#B5B5B5]"
            />
          </div>
          {errors.name && <p className="text-xs text-[#E76F51] mt-1" data-testid="signup-name-error">{errors.name}</p>}
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-bold text-[#2B2D42] mb-2">Email Address</label>
          <div className={inputCls(errors.email)}>
            <Mail className="w-5 h-5 text-[#B5B5B5]" />
            <input
              type="email"
              data-testid="signup-email-input"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setErrors({ ...errors, email: undefined }); }}
              placeholder="you@example.com"
              autoComplete="email"
              className="flex-1 ml-3 outline-none bg-transparent text-[#2B2D42] placeholder-[#B5B5B5]"
            />
          </div>
          {errors.email && <p className="text-xs text-[#E76F51] mt-1" data-testid="signup-email-error">{errors.email}</p>}
        </div>

        {/* Password */}
        <div>
          <label className="block text-sm font-bold text-[#2B2D42] mb-2">Password</label>
          <div className={inputCls(errors.password)}>
            <Lock className="w-5 h-5 text-[#B5B5B5]" />
            <input
              type={showPassword ? 'text' : 'password'}
              data-testid="signup-password-input"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setErrors({ ...errors, password: undefined }); }}
              placeholder="At least 8 characters"
              autoComplete="new-password"
              className="flex-1 ml-3 outline-none bg-transparent text-[#2B2D42] placeholder-[#B5B5B5]"
            />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="text-[#B5B5B5]" data-testid="signup-toggle-password">
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          {errors.password && <p className="text-xs text-[#E76F51] mt-1" data-testid="signup-password-error">{errors.password}</p>}
        </div>

        {/* Confirm */}
        <div>
          <label className="block text-sm font-bold text-[#2B2D42] mb-2">Confirm Password</label>
          <div className={inputCls(errors.confirm)}>
            <Lock className="w-5 h-5 text-[#B5B5B5]" />
            <input
              type={showPassword ? 'text' : 'password'}
              data-testid="signup-confirm-password-input"
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); setErrors({ ...errors, confirm: undefined }); }}
              placeholder="Re-enter password"
              autoComplete="new-password"
              className="flex-1 ml-3 outline-none bg-transparent text-[#2B2D42] placeholder-[#B5B5B5]"
            />
          </div>
          {errors.confirm && <p className="text-xs text-[#E76F51] mt-1" data-testid="signup-confirm-error">{errors.confirm}</p>}
        </div>

        {/* Consent */}
        <div className="bg-[#FFF8F5] border-2 border-[#FFE5DD] rounded-2xl p-5 mt-6 space-y-4" data-testid="signup-consent-block">
          <div className="flex items-center gap-2 pb-2 border-b-2 border-[#FFE5DD]">
            <ShieldCheck className="w-5 h-5 text-[#2A9D8F]" />
            <span className="font-bold text-[#2B2D42] text-base">Legal Agreements <span className="text-[#C8553D]">*</span></span>
          </div>
          <Checkbox checked={termsAccepted} onChange={(v) => { setTermsAccepted(v); setErrors({ ...errors, consent: undefined }); }} testId="signup-terms-checkbox">
            I agree to the <Link to="/terms" className="font-bold text-[#C8553D] underline">Terms of Service</Link> and{' '}
            <Link to="/privacy" className="font-bold text-[#C8553D] underline">Privacy Policy</Link>.
          </Checkbox>
          <Checkbox checked={guidelinesAccepted} onChange={(v) => { setGuidelinesAccepted(v); setErrors({ ...errors, consent: undefined }); }} testId="signup-guidelines-checkbox">
            I agree to follow the <Link to="/community-guidelines" className="font-bold text-[#C8553D] underline">Community Guidelines</Link> and{' '}
            <Link to="/safety" className="font-bold text-[#C8553D] underline">Safety Rules</Link>.
          </Checkbox>
          <Checkbox checked={verificationConsent} onChange={(v) => { setVerificationConsent(v); setErrors({ ...errors, consent: undefined }); }} testId="signup-verification-checkbox">
            I consent to AfroConnect using my submitted photos for my dating profile.
          </Checkbox>
          {errors.consent && (
            <div className="flex items-center gap-2 bg-[#FFEBEE] p-2 rounded text-xs text-[#E76F51]" data-testid="signup-consent-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {errors.consent}
            </div>
          )}
        </div>

        <button
          type="submit"
          data-testid="signup-submit-button"
          disabled={loading}
          className="w-full bg-[#C8553D] hover:bg-[#9E3A26] disabled:opacity-60 text-white font-bold py-4 rounded-full transition-colors flex items-center justify-center gap-2 shadow-lg shadow-[#C8553D]/30 mt-6"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create Account'}
        </button>
      </form>

      {/* Divider */}
      <div className="my-6 flex items-center gap-3">
        <div className="h-px flex-1 bg-[#F4F1DE]" />
        <span className="text-xs font-bold uppercase tracking-wider text-[#B5B5B5]">or</span>
        <div className="h-px flex-1 bg-[#F4F1DE]" />
      </div>

      <GoogleSignInButton label="Sign up with Google" testId="signup-google-button" />
      <p className="mt-3 text-center text-xs text-[#6D6875]">
        By continuing with Google, you agree to our{' '}
        <Link to="/terms" className="font-bold text-[#C8553D] underline">Terms</Link> and{' '}
        <Link to="/privacy" className="font-bold text-[#C8553D] underline">Privacy Policy</Link>.
      </p>

      <div className="mt-6 text-center pb-12">
        <span className="text-sm text-[#6D6875]">Already have an account? </span>
        <Link to="/app/login" className="text-sm font-bold text-[#C8553D]" data-testid="signup-login-link">
          Log in
        </Link>
      </div>
    </AuthShell>
  );
}
